import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface HeatmapPoint {
  x: number
  y: number
  value: number
  count?: number
  spread?: number
  target?: string | null
  secondary?: string | null
  screen_size_range?: string
}

interface HeatmapOverlayProps {
  heatmapData: HeatmapPoint[]
  imageWidth: number
  imageHeight: number
  imageLeft: number
  imageTop: number
  pageUrl: string
  deviceType: 'mobile' | 'desktop'
  opacity?: number
  visible?: boolean
  /** Click on a hot point (the capture opens that element's popover). */
  onPointClick?: (point: HeatmapPoint) => void
}

export type HeatmapOverlayHandle = {
  getHaloCanvas: () => HTMLCanvasElement | null
  getMainCanvas: () => HTMLCanvasElement | null
}

type Rgb = { r: number; g: number; b: number }

/**
 * One warm scale from the chart tokens (Paper legend "Fewer clicks → More
 * clicks"): --primary-tint-ring → --chart-1 → --chart-2. Canvas can't read CSS
 * variables, so each token is painted once into a 1px canvas and read back as
 * RGB; the fallbacks are the light-theme values for environments without a
 * canvas (tests, SSR).
 */
const SCALE_TOKENS: Array<[string, Rgb]> = [
  ['--primary-tint-ring', { r: 255, g: 225, b: 204 }],
  ['--chart-1', { r: 244, g: 112, b: 30 }],
  ['--chart-2', { r: 206, g: 69, b: 10 }],
]

function resolveTokenRgb(token: string, fallback: Rgb): Rgb {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement).getPropertyValue(token).trim()
  if (!value) return fallback
  const probe = document.createElement('canvas')
  probe.width = probe.height = 1
  const ctx = probe.getContext('2d', { willReadFrequently: true })
  if (!ctx) return fallback
  ctx.fillStyle = '#000'
  ctx.fillStyle = value
  ctx.fillRect(0, 0, 1, 1)
  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data
  return { r, g, b }
}

function useHeatScale(): Rgb[] {
  const [scale] = useState(() => SCALE_TOKENS.map(([token, fallback]) => resolveTokenRgb(token, fallback)))
  return scale
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  }
}

export function getHeatmapColor(intensity: number, scale: Rgb[]): Rgb {
  const i = Math.max(0, Math.min(1, intensity))
  return i < 0.5 ? mix(scale[0], scale[1], i / 0.5) : mix(scale[1], scale[2], (i - 0.5) / 0.5)
}

/** Blob radius grows with the capture width so an 800px desktop map reads like Paper; phone captures keep a finger-sized floor. */
const baseRadiusFor = (width: number, height: number) => Math.max(width * 0.03, height * 0.005, 18)

export const HeatmapOverlay = forwardRef<HeatmapOverlayHandle, HeatmapOverlayProps>(
  (
    {
      heatmapData,
      imageWidth,
      imageHeight,
      imageLeft,
      imageTop,
      pageUrl,
      deviceType,
      opacity = 0.9,
      visible = true,
      onPointClick,
    },
    ref,
  ) => {
  const haloCanvasRef = useRef<HTMLCanvasElement>(null)
  const mainCanvasRef = useRef<HTMLCanvasElement>(null)
  const scale = useHeatScale()
  const [hoveredPoint, setHoveredPoint] = useState<HeatmapPoint | null>(null)
  const hasValidDimensions =
    Number.isFinite(imageWidth) &&
    Number.isFinite(imageHeight) &&
    imageWidth > 0 &&
    imageHeight > 0
  const canvasWidth = hasValidDimensions ? Math.max(1, Math.round(imageWidth)) : 0
  const canvasHeight = hasValidDimensions ? Math.max(1, Math.round(imageHeight)) : 0

  useImperativeHandle(ref, () => ({
    getHaloCanvas: () => haloCanvasRef.current,
    getMainCanvas: () => mainCanvasRef.current,
  }))

  useEffect(() => {
    const haloCanvas = haloCanvasRef.current
    const mainCanvas = mainCanvasRef.current

    if (!haloCanvas || !mainCanvas || heatmapData.length === 0 || !visible || !hasValidDimensions) return

    const haloCtx = haloCanvas.getContext('2d')
    const ctx = mainCanvas.getContext('2d')

    if (!haloCtx || !ctx) return

    haloCanvas.width = canvasWidth
    haloCanvas.height = canvasHeight
    mainCanvas.width = canvasWidth
    mainCanvas.height = canvasHeight

    haloCtx.clearRect(0, 0, haloCanvas.width, haloCanvas.height)
    ctx.clearRect(0, 0, mainCanvas.width, mainCanvas.height)

    const maxValue = Math.max(...heatmapData.map((p) => p.value), 10)

    // Convert percentage coordinates to pixel coordinates
    const points = heatmapData.map((point) => ({
      x: (point.x / 100) * canvasWidth,
      y: (point.y / 100) * canvasHeight,
      value: point.value,
      spread: point.spread ?? 0,
    }))

    const baseRadius = baseRadiusFor(canvasWidth, canvasHeight)

    // Draw halo effects first
    points.forEach((point) => {
      const intensity = Math.min(1, point.value / maxValue)
      // Weak points fade as well as shrink, so the top hotspots carry the map
      const strength = 0.15 + 0.85 * Math.sqrt(intensity)
      // Spread is in the same percentage-point units as x/y; convert to pixels
      const spreadPx = (point.spread / 100) * Math.min(canvasWidth, canvasHeight)
      const spreadBonus = Math.min(spreadPx, baseRadius * 3)
      const haloRadius = baseRadius * (1.5 + intensity * 2) + spreadBonus
      const { r, g, b } = getHeatmapColor(intensity, scale)

      const haloGradient = haloCtx.createRadialGradient(point.x, point.y, 0, point.x, point.y, haloRadius)
      haloGradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.4 * strength})`)
      haloGradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, ${0.2 * strength})`)
      haloGradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)

      haloCtx.beginPath()
      haloCtx.arc(point.x, point.y, haloRadius, 0, Math.PI * 2)
      haloCtx.fillStyle = haloGradient
      haloCtx.fill()
    })

    // Draw main heatmap points
    points.forEach((point) => {
      const intensity = Math.min(1, point.value / maxValue)
      const strength = 0.15 + 0.85 * Math.sqrt(intensity)
      const spreadPx = (point.spread / 100) * Math.min(canvasWidth, canvasHeight)
      const spreadBonus = Math.min(spreadPx * 0.6, baseRadius * 2)
      const radius = baseRadius * (0.8 + intensity * 1.2) + spreadBonus
      const { r, g, b } = getHeatmapColor(intensity, scale)

      const gradient = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, radius)
      gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.8 * strength})`)
      gradient.addColorStop(0.7, `rgba(${r}, ${g}, ${b}, ${0.3 * strength})`)
      gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`)

      ctx.beginPath()
      ctx.arc(point.x, point.y, radius, 0, Math.PI * 2)
      ctx.fillStyle = gradient
      ctx.fill()
    })
  }, [heatmapData, canvasWidth, canvasHeight, pageUrl, deviceType, visible, hasValidDimensions, scale])

  const pointAt = (clientX: number, clientY: number): HeatmapPoint | null => {
    const canvas = mainCanvasRef.current
    if (!canvas) return null
    const rect = canvas.getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top
    const maxValue = Math.max(...heatmapData.map((p) => p.value), 10)
    const baseRadius = baseRadiusFor(canvasWidth, canvasHeight)

    let best: HeatmapPoint | null = null
    let bestDistance = Infinity
    for (const point of heatmapData) {
      const px = (point.x / 100) * canvasWidth
      const py = (point.y / 100) * canvasHeight
      const intensity = Math.min(1, point.value / maxValue)
      const radius = baseRadius * (0.8 + intensity * 1.2)
      const distance = Math.hypot(x - px, y - py)
      if (distance <= radius && distance < bestDistance) {
        best = point
        bestDistance = distance
      }
    }
    return best
  }

  if (heatmapData.length === 0 || !visible || !hasValidDimensions) return null

  const totalClicks = heatmapData.reduce((sum, p) => sum + (p.count || p.value || 1), 0)
  const hoveredClicks = hoveredPoint ? hoveredPoint.count || hoveredPoint.value || 1 : 0
  const unit = deviceType === 'mobile' ? 'tap' : 'click'

  return (
    <>
      {/* Halo canvas */}
      <canvas
        ref={haloCanvasRef}
        width={canvasWidth}
        height={canvasHeight}
        className="absolute pointer-events-none z-[1] mix-blend-multiply"
        style={{
          top: `${imageTop}px`,
          left: `${imageLeft}px`,
          width: `${canvasWidth}px`,
          height: `${canvasHeight}px`,
          opacity: opacity * 0.5,
        }}
      />

      {/* Main heatmap canvas */}
      <TooltipProvider>
        <Tooltip open={!!hoveredPoint}>
          <TooltipTrigger asChild>
            <canvas
              ref={mainCanvasRef}
              width={canvasWidth}
              height={canvasHeight}
              className={hoveredPoint && onPointClick ? 'absolute z-[2] cursor-pointer' : 'absolute z-[2]'}
              style={{
                top: `${imageTop}px`,
                left: `${imageLeft}px`,
                width: `${canvasWidth}px`,
                height: `${canvasHeight}px`,
                opacity,
              }}
              onMouseMove={(e) => setHoveredPoint(pointAt(e.clientX, e.clientY))}
              onMouseLeave={() => setHoveredPoint(null)}
              onClick={(e) => {
                const point = pointAt(e.clientX, e.clientY)
                if (point) onPointClick?.(point)
              }}
            />
          </TooltipTrigger>
          {hoveredPoint && (
            <TooltipContent className="max-w-xs">
              <div className="text-xs">
                <span className="font-medium">
                  {hoveredClicks} {unit}
                  {hoveredClicks !== 1 ? 's' : ''}
                </span>
                <span className="ml-1 text-muted-foreground">
                  ({((hoveredClicks / totalClicks) * 100).toFixed(1)}%)
                </span>
                {hoveredPoint.target && <div className="mt-0.5 text-muted-foreground">{hoveredPoint.target}</div>}
              </div>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
    </>
  )
})

HeatmapOverlay.displayName = 'HeatmapOverlay'
